<template>
  <div class="h-screen min-w-90 flex flex-col pb-safe">
    <header class="font-ds min-h-14 shrink-0 flex items-center gap-0.5 px-1.5 pt-safe-offset-2 pb-2 bg-card border-b border-border">
      <DSButton
        size="icon"
        variant="ghost"
        class="size-11 text-foreground hover:bg-background "
        aria-label="Open navigation menu"
        @click="drawerOpen = true"
      >
        <MenuIcon
          class="size-5.5!"
          aria-hidden="true"
        />
      </DSButton>
      <slot name="header" />
      <div class="flex-1" />
      <PowerModeSheet
        v-if="isPowerMode"
      >
        <template #default="{ openPowerModeSheet }">
          <DSHeaderPowerModeBadge
            is-mobile
            @click="openPowerModeSheet"
          />
        </template>
      </PowerModeSheet>
      <DSButton
        v-if="!isEmptyApp"
        size="icon"
        variant="ghost"
        class="size-11 text-muted-foreground hover:bg-background"
        aria-label="Open search"
        @click="handleOpenSearch"
      >
        <SearchIcon
          class="size-5.25!"
          aria-hidden="true"
        />
      </DSButton>
      <NotificationsSheet>
        <template #default="{ openNotificationsSheet }">
          <DSHeaderNotificationsButton
            has-unread
            is-mobile
            @click="openNotificationsSheet"
          />
        </template>
      </NotificationsSheet>
    </header>
    <AppDisruptionMessage v-if="isDisruptionMessageVisible" />
    <div
      v-if="isRefreshButtonVisible"
      class="px-3 py-2"
    >
      <DSButton
        variant="secondary"
        class="w-full"
        @click="handleRefreshAnalytics"
      >
        <RotateCwIcon
          class="size-3.5!"
          aria-hidden="true"
        />
        Refresh analytics
      </DSButton>
    </div>
    <slot
      :drawer-open
      :update-drawer-open="(value: boolean) => drawerOpen = value"
      :is-empty-app
    />
    <AppAlerts />
    <AppAnalyticsProgressBar />
    <main class="flex flex-col flex-auto px-4 sm:px-6 overflow-hidden">
      <AppWelcomeScreen
        v-if="isWelcomeScreenVisible"
        :is-empty-app
        :is-empty-analytics
        :is-empty-connectors
      />
      <slot
        v-else
        name="content"
      />
    </main>
  </div>
</template>

<script setup lang="ts">
import { ComputedRef, inject, markRaw, ref } from 'vue'

import { POWER_MODE } from '@/const'
import { IS_DISRUPTION_MESSAGE_VISIBLE } from '../utils/const'

import { useModalsStore } from '@/store/modals'

import { useMainWrapper } from '../utils/useMainWrapper'

import NotificationsSheet from './NotificationsSheet.vue'
import PowerModeSheet from './PowerModeSheet.vue'
import SearchDialog from './SearchDialog.vue'

import { useRefreshButton } from '../utils/useRefreshButton'
import { AppAlerts, AppAnalyticsProgressBar, AppDisruptionMessage, AppWelcomeScreen } from '@app'
import { DSButton, DSHeaderNotificationsButton, DSHeaderPowerModeBadge } from '@ds'
import { MenuIcon, RotateCwIcon, SearchIcon } from '@lucide/vue'

type Props = {
  disabled?: boolean
  repositoryName?: string
}

defineProps<Props>()

const drawerOpen = ref(false)

const { isEmptyApp, isEmptyAnalytics, isEmptyConnectors, isWelcomeScreenVisible } = useMainWrapper()

const modalsStore = useModalsStore()

const { isRefreshButtonVisible, handleRefreshAnalytics } = useRefreshButton()

const isDisruptionMessageVisible = inject<boolean>(IS_DISRUPTION_MESSAGE_VISIBLE, false)
const isPowerMode = inject<ComputedRef<boolean>>(POWER_MODE)

const handleOpenSearch = () => {
  const modalInstance = modalsStore.init('global-search', markRaw(SearchDialog))
  modalInstance?.open(modalsStore.getZIndex())
}
</script>
